
import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TrendBadge from './trend-badge';
import SmaSlopeBadge from './sma-slope-badge';
import { formatPrice, formatMarketCap, formatVolume, formatPercentChange } from '../utils/format-helpers';
import { toggleWatchlist, isInWatchlist } from '../services/watchlist-service';
import './market-table-row.css';

function MarketTableRow({ coin, onClick }) {
  const navigate = useNavigate();
  const [isInWatchlistState, setIsInWatchlistState] = useState(false);
  const [isWatchlistLoading, setIsWatchlistLoading] = useState(false);

  React.useEffect(() => {
    async function checkWatchlistStatus() {
      try {
        const inWatchlist = await isInWatchlist(coin.id);
        setIsInWatchlistState(inWatchlist);
      } catch (error) {
        console.error('Error checking watchlist status:', error);
      }
    }

    if (coin.id) {
      checkWatchlistStatus();
    }
  }, [coin.id]);

  const handleRowClick = (e) => {
    if (e.target.closest('.watchlist-btn')) {
      return;
    }
    if (onClick) {
      onClick(coin);
    } else {
      navigate('/coin/' + coin.id);
    }
  };

  const handleWatchlistToggle = async (e) => {
    e.stopPropagation();
    if (isWatchlistLoading) return;
    try {
      setIsWatchlistLoading(true);
      const newStatus = await toggleWatchlist(coin.id);
      setIsInWatchlistState(newStatus);
    } catch (error) {
      console.error('Error toggling watchlist:', error);
    } finally {
      setIsWatchlistLoading(false);
    }
  };

  const logoLetter = coin.symbol ? coin.symbol.charAt(0).toUpperCase() : '?';
  // Tao Subnet prices/market caps come from taostats in TAO, not USD — show a τ suffix instead of $.
  const isTaoSubnet = typeof coin.id === 'string' && coin.id.startsWith('subnet-');
  const currencySymbol = isTaoSubnet ? 'τ' : '$';
  const priceChangeClass = coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative';

  // Volume colour:
  //   Spiking (>1.5× avg) + price up   → volume-spike-up   (bright green)
  //   Spiking (>1.5× avg) + price down → volume-spike-down  (bright red)
  //   Otherwise                         → volume-neutral     (dim, no colour)
  const priceUp = coin.price_change_percentage_24h > 0;
  const isSpiking = coin.volumeRatio != null && coin.volumeRatio >= 1.5;
  const volumeClass = isSpiking
    ? (priceUp ? 'volume-spike-up' : 'volume-spike-down')
    : 'volume-neutral';

  // Recent spike (last 10 days, not today): same >=2x-average threshold as
  // today's volumeSpike flag, checked against the last 10 days of history.
  // Only shown when today isn't already spiking — today's spike takes priority.
  const last10Volumes = coin.volumeHistory30d ? coin.volumeHistory30d.slice(-10) : [];
  const recentSpike = !coin.volumeSpike && coin.volumeAvg200 != null &&
    last10Volumes.some(v => v != null && v >= 2 * coin.volumeAvg200);

  return (
    <tr className="market-table-row" onClick={handleRowClick}>
      <td className="watchlist-cell">
        <button
          className={'watchlist-btn' + (isInWatchlistState ? ' active' : '') + (isWatchlistLoading ? ' loading' : '')}
          onClick={handleWatchlistToggle}
          disabled={isWatchlistLoading}
          aria-label={isInWatchlistState ? 'Remove from watchlist' : 'Add to watchlist'}
        >
          {isWatchlistLoading ? (
            <span className="watchlist-spinner">&#8987;</span>
          ) : isInWatchlistState ? (
            '★'
          ) : (
            '☆'
          )}
        </button>
      </td>

      <td className="rank-cell">
        <span className="rank-number">{coin.market_cap_rank || '-'}</span>
      </td>

      <td className="name-cell">
        <div className="coin-info">
          <div className="coin-logo">
            {coin.image ? (
              <img
                src={coin.image}
                alt={coin.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="coin-logo-fallback" style={{ display: coin.image ? 'none' : 'flex' }}>
              {logoLetter}
            </div>
          </div>
          <div className="coin-details">
            <div className="coin-name">{coin.name || 'Unknown'}</div>
            <div className="coin-symbol">{coin.symbol ? coin.symbol.toUpperCase() : 'N/A'}</div>
          </div>
        </div>
      </td>

      <td className="price-cell">
        <span className="price-value">{formatPrice(coin.current_price, currencySymbol)}</span>
      </td>

      <td className={'change-cell ' + priceChangeClass}>
        <span className="change-value">{formatPercentChange(coin.price_change_percentage_24h)}</span>
      </td>

      <td className="market-cap-cell">
        <span className="market-cap-value">{formatMarketCap(coin.market_cap, currencySymbol)}</span>
      </td>

      <td className={'volume-cell' + (recentSpike ? ' volume-cell-recent-spike' : '')}>
        <div className={'volume-value ' + volumeClass}>
          {coin.volumeSpike && (
            <span className="volume-spike" title="Volume spike — more than 2x the 200-day average">&#9889;</span>
          )}
          {recentSpike && (
            <span className="volume-spike volume-spike-recent" title="Volume spiked 2x+ avg within the last 10 days (not today)">&#9889;</span>
          )}
          {formatVolume(coin.volumeLatest ?? coin.total_volume, currencySymbol)}
        </div>
        {coin.volumeRatio != null && (
          <div className="volume-ratio">{coin.volumeRatio.toFixed(1)}x avg</div>
        )}
      </td>

      <td className="trend-cell">
        <TrendBadge trend={coin.trends ? coin.trends.weekly : coin.weeklyTrend} />
      </td>

      <td className="trend-cell">
        <TrendBadge trend={coin.trends ? coin.trends.daily : coin.dailyTrend} />
      </td>

      <td className="trend-cell">
        <TrendBadge trend={coin.trends ? coin.trends.fourHour : coin.fourHourTrend} />
      </td>

      <td className="trend-cell sma-slope-cell">
        <SmaSlopeBadge
          weekly={coin.indicators?.weekly?.sma50Slope}
          daily={coin.indicators?.daily?.sma50Slope}
          fourHour={coin.indicators?.fourHour?.sma50Slope}
        />
      </td>
    </tr>
  );
}

export default memo(MarketTableRow);
